import authService from '@/api/authService';
import jobApi from '@/api/jobApi';
import ErrorAlert from '@/components/errorAlert';
import NavigationBar from '@/components/navigationBar';
import { PageTitle } from '@/components/pageTitle';
import {
    Alert,
    Badge,
    Box,
    Breadcrumbs,
    Button,
    Modal,
    Switch,
    Table as MTable,
} from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconFileText, IconHome } from '@tabler/icons-react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { hasClaim } from 'common/helpers/claimsHelpers';
import { JobRunState, type JobRun } from 'common/types/jobRun';
import { useState } from 'react';
import {
    DateFilter,
    type DateRange,
    endOfDay,
    SelectFilter,
    startOfDay,
    Table,
    TextFilter,
} from 'schummar-table';
import { useTranslator } from 'translations';

export const Route = createFileRoute('/_auth/jobs')({
    component: RouteComponent,

    async beforeLoad({ location }) {
        const login = await authService.requireLogin(location.href);
        if (!hasClaim(login.user, 'jobs.read')) {
            throw redirect({ to: '/' });
        }
    },
});

const stateColors: Record<JobRunState, string> = {
    planned: 'gray',
    running: 'blue',
    retrying: 'orange',
    completed: 'green',
    error: 'red',
};

function RouteComponent() {
    const t = useTranslator();
    const [range, setRange] = useState<DateRange | null>(() => ({
        min: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        max: endOfDay(new Date()),
    }));
    const [states, setStates] = useState<Set<JobRunState>>();
    const [hideFrequent, setHideFrequent] = useState(true);
    const [logsRun, setLogsRun] = useState<JobRun>();

    const [current, error] = jobApi
        .list({
            from: (range?.min ?? new Date(0)).toISOString(),
            ...(range && range.max < new Date() && { to: range.max.toISOString() }),
            hideFrequent: String(hideFrequent),
        })
        .useCache();

    // Keeps showing the previous result while a changed filter refetches.
    const [data, setData] = useState(current);
    if (current && current !== data) {
        setData(current);
    }

    function showFailed() {
        if (!data) return;
        setRange({ min: data.failedSince, max: endOfDay(new Date()) });
        setStates(new Set(['error']));
        setHideFrequent(false);
    }

    function openLogs(run: JobRun) {
        jobApi.history(run.id).invalidate();
        setLogsRun(run);
    }

    const stateBadge = (state: JobRunState) => (
        <Badge color={stateColors[state]} variant="light">
            {t(`jobs.states.${state}`)}
        </Badge>
    );

    const dateTime = (date: Date | null) =>
        date ? t.dateTimeFormat(date, { dateStyle: 'medium', timeStyle: 'medium' }) : '-';

    return (
        <PageTitle title={t('jobs.title')}>
            <NavigationBar
                breadcrumbs={
                    <Breadcrumbs>
                        <Link to="/">
                            <IconHome />
                        </Link>
                        <Link to="/jobs">{t('jobs.title')}</Link>
                    </Breadcrumbs>
                }
            />

            <Box
                css={{
                    padding: '0 var(--mantine-spacing-md)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 'var(--mantine-spacing-md)',
                }}
            >
                {data &&
                    (data.failedLast30Days > 0 ? (
                        <Alert color="red" icon={<IconAlertTriangle />} css={{ flex: '1 1 auto' }}>
                            <Box
                                css={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 'var(--mantine-spacing-md)',
                                }}
                            >
                                {t('jobs.failedSummary', { count: data.failedLast30Days })}
                                <Button size="xs" color="red" onClick={showFailed}>
                                    {t('jobs.showFailed')}
                                </Button>
                            </Box>
                        </Alert>
                    ) : (
                        <Alert color="green" icon={<IconCircleCheck />} css={{ flex: '1 1 auto' }}>
                            {t('jobs.noFailures')}
                        </Alert>
                    ))}

                <Switch
                    checked={hideFrequent}
                    onChange={(event) => setHideFrequent(event.currentTarget.checked)}
                    label={t('jobs.hideFrequent')}
                />
            </Box>

            {!!error && <ErrorAlert error={error} css={{ margin: 'var(--mantine-spacing-md)' }} />}

            <Box css={{ flex: '1 0 0', overflowX: 'auto', marginTop: 'var(--mantine-spacing-md)' }}>
                <Table
                    items={data?.runs ?? []}
                    id="id"
                    fullWidth="left"
                    virtual
                    defaultSort={[{ columnId: 'start', direction: 'desc' }]}
                    rowAction={(run) => (
                        <Button
                            size="compact-xs"
                            variant="subtle"
                            leftSection={<IconFileText size={14} />}
                            onClick={() => openLogs(run)}
                        >
                            {t('jobs.logs')}
                        </Button>
                    )}
                    columns={(col) => [
                        col((run) => run.jobId, {
                            id: 'job',
                            header: t('jobs.job'),
                            filter: <SelectFilter />,
                            width: 'minmax(150px, 250px)',
                        }),

                        col((run) => run.state, {
                            id: 'state',
                            header: t('jobs.state'),
                            renderCell: stateBadge,
                            filter: (
                                <SelectFilter
                                    value={states}
                                    onChange={setStates}
                                    options={JobRunState.options}
                                    stringValue={(state) => t(`jobs.states.${state}`)}
                                    render={stateBadge}
                                />
                            ),
                            sortBy: [(state) => JobRunState.options.indexOf(state)],
                            width: 'max-content',
                        }),

                        col((run) => run.startedOn ?? run.nextRun, {
                            id: 'start',
                            header: t('jobs.start'),
                            renderCell: dateTime,
                            filter: (
                                <DateFilter
                                    external
                                    value={range}
                                    onChange={(value) =>
                                        setRange(
                                            value instanceof Date
                                                ? { min: startOfDay(value), max: endOfDay(value) }
                                                : (value ?? null),
                                        )
                                    }
                                />
                            ),
                            width: 'max-content',
                        }),

                        col((run) => run.finishedOn, {
                            id: 'end',
                            header: t('jobs.end'),
                            renderCell: dateTime,
                            width: 'max-content',
                        }),

                        col((run) => run.durationMs, {
                            id: 'duration',
                            header: t('jobs.duration'),
                            renderCell: (ms) =>
                                ms === null
                                    ? '-'
                                    : t('jobs.durationSeconds', {
                                          seconds: Math.round(ms / 100) / 10,
                                      }),
                            width: 'max-content',
                        }),

                        col(
                            (run) =>
                                run.attempt +
                                (run.state === 'planned' || run.state === 'retrying' ? 0 : 1),
                            {
                                id: 'attempts',
                                header: t('jobs.attempts'),
                                width: 'max-content',
                            },
                        ),

                        col((run) => run.error ?? '', {
                            id: 'error',
                            header: t('jobs.error'),
                            renderCell: (message) => (
                                <span
                                    title={message}
                                    css={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {message}
                                </span>
                            ),
                            filter: <TextFilter />,
                            width: 'minmax(200px, 1fr)',
                        }),
                    ]}
                />

                {!data && !error && (
                    <Box css={{ textAlign: 'center', padding: 'var(--mantine-spacing-md)' }}>
                        {t('general.loading')}
                    </Box>
                )}
            </Box>

            {logsRun && <JobLogsModal run={logsRun} onClose={() => setLogsRun(undefined)} />}
        </PageTitle>
    );
}

function JobLogsModal({ run, onClose }: { run: JobRun; onClose: () => void }) {
    const t = useTranslator();
    const [history, error] = jobApi.history(run.id).useCache();

    return (
        <Modal opened onClose={onClose} size="xl" title={t('jobs.logsTitle', { jobId: run.jobId })}>
            {error ? (
                <ErrorAlert error={error} />
            ) : !history ? (
                t('general.loading')
            ) : history.length === 0 ? (
                t('jobs.noLogs')
            ) : (
                <MTable striped>
                    <MTable.Thead>
                        <MTable.Tr>
                            <MTable.Th>{t('jobs.log.time')}</MTable.Th>
                            <MTable.Th>{t('jobs.log.attempt')}</MTable.Th>
                            <MTable.Th>{t('jobs.log.event')}</MTable.Th>
                            <MTable.Th>{t('jobs.log.message')}</MTable.Th>
                        </MTable.Tr>
                    </MTable.Thead>
                    <MTable.Tbody>
                        {history.map((item, index) => (
                            <MTable.Tr key={index}>
                                <MTable.Td css={{ whiteSpace: 'nowrap' }}>
                                    {t.dateTimeFormat(new Date(item.t), {
                                        dateStyle: 'short',
                                        timeStyle: 'medium',
                                    })}
                                </MTable.Td>
                                <MTable.Td>{item.attempt + 1}</MTable.Td>
                                <MTable.Td css={{ minWidth: 100 }}>
                                    <Badge
                                        variant="light"
                                        color={
                                            item.level === 'error'
                                                ? 'red'
                                                : item.level === 'warn'
                                                  ? 'orange'
                                                  : 'gray'
                                        }
                                    >
                                        {item.event === 'log' ? item.level : item.event}
                                    </Badge>
                                </MTable.Td>
                                <MTable.Td
                                    css={{
                                        fontFamily: 'var(--mantine-font-family-monospace)',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {item.message}
                                </MTable.Td>
                            </MTable.Tr>
                        ))}
                    </MTable.Tbody>
                </MTable>
            )}
        </Modal>
    );
}
